import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon, PhoneIcon } from 'lucide-react';
import { ButtonLink } from '../ui/ButtonLink';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { EASE_OUT } from '../../utils/motion';
import { contact, mainNav, QUOTE_PATH } from '../../data/site';
interface MobileMenuProps {
  open: boolean;
  onClose: () => void;
}
const linkClass = (active: boolean) => `block py-4 font-display text-[22px] font-semibold uppercase tracking-[0.06em] ${active ? 'text-white' : 'text-steel-200'}`;
export function MobileMenu({
  open,
  onClose
}: MobileMenuProps) {
  const location = useLocation();
  const aboutActive = location.pathname.startsWith('/about');
  const [aboutOpen, setAboutOpen] = useState(aboutActive);
  useBodyScrollLock(open);
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);
  return <AnimatePresence data-id="element-410" data-mp="9x7utowvwjbj">
      {open && <motion.div id="mobile-menu" initial={{
      opacity: 0,
      y: -8
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: -8
    }} transition={{
      duration: 0.2,
      ease: EASE_OUT
    }} className="fixed inset-x-0 bottom-0 top-[96px] z-40 overflow-y-auto bg-charcoal-950 lg:hidden" data-id="element-411" data-mp="9yix1kaolj0j">
          <nav aria-label="Mobile" className="px-5 pb-12 pt-2 sm:px-8" data-id="element-412" data-mp="3mdacs945irok">
            <ul className="divide-y divide-white/10 border-b border-white/10" data-id="element-413" data-mp="3rcn00uyxq0ro">
              {mainNav.map(item => item.children ? <li key={item.label} data-id="element-414" data-mp="11mt34nhodc6n">
                    <div className="flex items-center justify-between" data-id="element-415" data-mp="16mfesshuukdc">
                      <NavLink to={item.to} onClick={onClose} className={linkClass(aboutActive)} data-id="element-416" data-mp="16kkxrp9pfcej">
                        {item.label}
                      </NavLink>
                      <button type="button" aria-expanded={aboutOpen} aria-controls="mobile-about-menu" onClick={() => setAboutOpen(value => !value)} className="flex h-12 w-12 items-center justify-center text-steel-300" data-id="element-417" data-mp="2ymxd83wk59ol">
                        <ChevronDownIcon className={`h-5 w-5 transition-transform duration-200 ease-out ${aboutOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-418" data-mp="2f09fljhbwmmy" />
                        <span className="sr-only" data-id="element-419" data-mp="2oxdrmqhgn1q1">{aboutOpen ? 'Hide' : 'Show'} About Us pages</span>
                      </button>
                    </div>
                    {aboutOpen && <ul id="mobile-about-menu" className="mb-4 border-l-2 border-brand-red pl-4" data-id="element-420" data-mp="1w8gnrsdzx7gt">
                        {item.children.map(child => <li key={child.to} data-id="element-421" data-mp="gi5x5768r81o">
                            <NavLink to={child.to} onClick={onClose} className={({
                  isActive
                }) => `block py-2.5 text-[17px] ${isActive ? 'font-semibold text-white' : 'text-steel-300'}`} data-id="element-422" data-mp="gixscgvxf3rf">
                              {child.label}
                            </NavLink>
                          </li>)}
                      </ul>}
                  </li> : <li key={item.label} data-id="element-423" data-mp="qf0sjh77wsbq">
                    <NavLink to={item.to} end={item.to === '/'} onClick={onClose} className={({
              isActive
            }) => linkClass(isActive)} data-id="element-424" data-mp="qi6kr5b58abo">
                      {item.label}
                    </NavLink>
                  </li>)}
            </ul>
            <ButtonLink to={QUOTE_PATH} size="lg" className="mt-8 w-full" onClick={onClose} data-id="element-425" data-mp="3lo6k3viao2f1">
              Request a Quote
            </ButtonLink>
            <div className="mt-6 space-y-3" data-id="element-426" data-mp="3cpcpubsfro4n">
              {[contact.phone, contact.mobile].map(line => <a key={line.href} href={line.href} className="flex items-center gap-3 text-steel-200" data-id="element-427" data-mp="d9xyj7e6wbwd">
                  <PhoneIcon className="h-4 w-4 text-brand-light" aria-hidden="true" data-id="element-428" data-mp="8bo10q4zsz7l" />
                  <span data-id="element-429" data-mp="48htzga23402">
                    <span className="sr-only" data-id="element-430" data-mp="47oki7jpq0e8">{line.label}: </span>
                    {line.display}
                  </span>
                </a>)}
            </div>
          </nav>
        </motion.div>}
    </AnimatePresence>;
}